/* eslint-disable react/prop-types */
import React, { useMemo, useEffect } from 'react';
import { StyleSheet, View, TouchableOpacity } from 'react-native';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Toast from 'react-native-toast-message';

import { showAlert } from '@/store/global';
import { showPopup } from '@/store/modal';

import { Container, Title } from '@components/screen/Layout';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import Button from '@components/form/Button';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import ActionButton from '@components/navbar/ActionButton';
import Image from '@components/global/Image';

import Colors from '@global/colors';
import { isWeb } from '@global/screens';
import { hasNoAccess, generateConstant, getConstant } from '@global/helpers';
import { deleteMatrixApproval } from '@api/ms-b2b';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetMatrixApprovalListQuery,
  useGetMatrixApprovalCountQuery,
  useGetMatrixApprovalInfiniteListInfiniteQuery,
} from '@api/listApi';

// Search is by the matrix-approval LABEL, but the API stores `applicable_for` as a
// value — remap the $or search regex from label → value (was the old remapFilter).
const toApiFilter = (rawFilter = {}) => {
  const filter = { ...rawFilter };
  if (Array.isArray(filter.$or)) {
    const values = getConstant('matrix_approval');
    filter.$or = filter.$or.map((f) => {
      if (f?.applicable_for?.$regex) {
        const val = String(f.applicable_for.$regex).toLowerCase();
        const matrix = values.find((m) => m.label.toLowerCase().includes(val));
        if (matrix) {
          return {
            ...f,
            applicable_for: { ...f.applicable_for, $regex: matrix.value },
          };
        }
      }
      return f;
    });
  }
  return filter;
};

const MatrixApprovalScreen = (props) => {
  // ── selectors ──
  const dispatch = useDispatch();

  // ── derived ──
  const hasAccess = !hasNoAccess([
    'superadmin',
    'sales_admin',
    'marketing_admin',
    'general',
  ]);

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetMatrixApprovalListQuery,
    countHook: useGetMatrixApprovalCountQuery,
    infiniteHook: useGetMatrixApprovalInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const structures = useMemo(() => {
    const centerHeadStyle = { textAlign: 'center' };
    const cols = {
      name: {
        head: 'Matrix Approval Type',
        body: 'name',
        headStyle: centerHeadStyle,
      },
      tier_count: {
        head: 'Matrix Tier',
        body: 'tier_count',
        headStyle: centerHeadStyle,
      },
      approver_count: {
        head: 'Approval Level',
        body: 'approver_count',
        headStyle: centerHeadStyle,
      },
      description: {
        head: 'Description',
        body: 'description',
        headStyle: centerHeadStyle,
      },
    };
    if (hasAccess) {
      cols.action = {
        head: 'Action',
        body: '',
        type: 'action_icon',
        actions: [
          { name: 'edit', icon: require('@assets/img/icons/edit-grey.svg') },
          { name: 'remove', icon: require('@assets/img/icons/trash-grey.svg') },
        ],
        headStyle: centerHeadStyle,
      };
    }
    return cols;
  }, [hasAccess]);

  // applicable_for (value) → display name (label); was the old generateData()
  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map((dt) => {
      const name = generateConstant(
        'matrix_approval',
        dt.applicable_for,
        'value',
      );
      return { ...dt, name: name.label };
    });
  }, [list.data]);

  // ── effects ──
  // Header "+ Add" (mobile) / notification (web) button.
  useEffect(() => {
    props.navigation.setOptions({
      headerRight: () =>
        isWeb ? (
          <NotificationSettingButton {...props} />
        ) : hasAccess ? (
          <ActionButton
            icon={require('@assets/img/icons/plus-white.svg')}
            onCallback={() =>
              navigateDetail({ id: 'add', name: '', prev_page: '', tab: '' })
            }
          />
        ) : (
          false
        ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation, hasAccess]);

  // ── handlers ──
  const navigateDetail = (navParams) => {
    openChild('MatrixApprovalNavigator', {
      screen: 'MatrixApprovalDetailScreen',
      params: navParams,
    });
  };

  const deleteMatirx = async (data) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const response = await deleteMatrixApproval(data._id);
      dispatch(showPopup({ name: 'isLoading', value: false }));
      if (response.status == 200) {
        Toast.show({ text1: 'Successfully deleted data', type: 'success' });
        list.refetch();
        count.refetch();
      }
    } catch (e) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({ text1: 'A system error occurred', type: 'error' });
    }
  };

  const callBackRowPress = ({ item }) => {
    if (!item || !item._id) {
      return false;
    }
    navigateDetail({ id: item._id, name: item.name, prev_page: '', tab: '' });
  };

  const callBackAction = (data) => {
    if (data.name === 'remove') {
      dispatch(
        showAlert({
          show: true,
          title: 'Remove Matrix Approval',
          description:
            'This matrix approval will be deleted. Do you want to proceed?',
          onCallback: () => deleteMatirx(data.data),
        }),
      );
    } else if (data.name === 'edit') {
      navigateDetail({
        id: data.data._id,
        name: data.data.name,
        prev_page: '',
        tab: '',
      });
    }
  };

  const renderItem = ({ data: item, index }) => (
    <TouchableOpacity
      onPress={() => callBackAction({ name: 'edit', data: item })}
      key={'list-matrix-' + index}
      style={styles.tableRow}>
      <SociollaText type="medium" style={styles.itemName}>
        {item.name}
      </SociollaText>
      <TouchableOpacity
        onPress={() => callBackAction({ name: 'remove', data: item })}
        style={styles.itemDelete}>
        <Image
          width={20}
          height={20}
          svg={true}
          source={require('@assets/img/icons/trash-grey.svg')}
          style={styles.itemDeleteIcon}
        />
      </TouchableOpacity>
      <View style={styles.row}>
        <View style={styles.flex}>
          <SociollaText style={styles.itemTitle}>Matrix Tier:</SociollaText>
          <SociollaText style={styles.itemValue}>
            {item.tier_count}
          </SociollaText>
        </View>
        <View style={styles.flex}>
          <SociollaText style={styles.itemTitle}>Approval Level:</SociollaText>
          <SociollaText style={styles.itemValue}>
            {item.approver_count}
          </SociollaText>
        </View>
      </View>
    </TouchableOpacity>
  );

  // ── render ──
  return (
    <Container>
      {isWeb && (
        <Title title="Matrix Approval">
          {hasAccess ? (
            <Button
              type="primary"
              rounded={true}
              buttonStyle={styles.buttonAdd}
              title={
                <SociollaText type="medium" style={styles.buttonText}>
                  + Add
                </SociollaText>
              }
              onPress={() =>
                navigateDetail({ id: 'add', name: '', prev_page: '', tab: '' })
              }
            />
          ) : (
            false
          )}
        </Title>
      )}
      {isWeb ? (
        <Table
          filterName={scope}
          name="matrix-approval"
          structures={structures}
          data={rows}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          loading={list.isLoading}
          refetching={list.isFetching}
          showPagination={true}
          showSearch={true}
          searchPlaceholder={'Search by type...'}
          searchKey={['applicable_for']}
          showFilter={false}
          hasNoHide={true}
          hasCallback={true}
          onClickRow={({ data }) => callBackRowPress({ item: data })}
          onMapAction={({ data, action }) =>
            callBackAction({ name: action.name, data })
          }
        />
      ) : (
        <TableMobile
          filterName={scope}
          data={rows}
          renderItem={renderItem}
          headerComponent={<></>}
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          totalData={count.data}
          showSearch={true}
          placeholder={'Search by type...'}
          searchKey={['applicable_for']}
        />
      )}
    </Container>
  );
};

const styles = StyleSheet.create({
  buttonAdd: {
    width: 100,
  },
  buttonText: {
    fontSize: 14,
    color: Colors.white,
  },
  flex: { flex: 1 },
  itemDelete: { position: 'absolute', top: 17, right: 20 },
  itemDeleteIcon: { height: 20, width: 20 },
  itemName: { paddingBottom: 10, fontSize: 16, color: Colors.black1 },
  itemTitle: { paddingBottom: 5, fontSize: 10, color: Colors.black5 },
  itemValue: { fontSize: 12, color: Colors.black2 },
  row: { flexDirection: 'row', width: '100%' },
  tableRow: {
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    minHeight: 93,
    width: '100%',
    flex: 1,
    borderBottomColor: Colors.grey9,
    borderBottomWidth: 1,
    padding: 16,
  },
});

MatrixApprovalScreen.propTypes = {
  children: PropTypes.node,
};

MatrixApprovalScreen.defaultProps = {
  children: <></>,
};

export default MatrixApprovalScreen;
